import { TaskTypeEnum } from '@/domains/wc-promotion/types';
import type { Product, SubProduct, ProductConditionParams } from '@nf/types/product';
import { thirdPartyProducts } from '@/settings/products/third-party-products';
import { useAuthToken } from '@/store/auth';
import { useEffect, useState, useMemo, useCallback, memo, Fragment } from 'react';
import { useAccountInfo } from '@/hooks/query/account';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { SwiperContainer } from '@/components/containers/swiper-container';
import { useTranslations } from 'next-intl';
import { triggerRewardSystemPanel } from '@nf/achievement-system-v2';
import { ProductProps } from '@/features/home-page/products';
import { wcPromotionMissionService } from '@/domains/wc-promotion/services/wc-promotion-mission.service';
import { isNumberGames, getForbiddenSport, sortVirtualSports } from '@/services/settings.service';
import { sportService } from '@/services/sport.service';
import { EventMode, menuDefinition } from '@/types/menu-setting';
import clsx from 'clsx';
import { homeStyle } from './home-page.style';
import { useRouter } from '@/navigation';
import { useLocale } from 'next-intl';
import { useAppearanceStore } from '@/store/appearance';
import { KafkaNote } from '@nf/utils-common/constants';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { ViewEnum } from '@/types';
import { useCasinoRoyale } from '@/hooks/query/use-casino-royale';
import { openExchangeInNewWindow, useOriginMenuData, MarketData } from '@/hooks/query';
import { useFilterSabaString } from '@/hooks/query/use-saba-filter';
import { useSiteStore } from '@/store/site';
import { sportsbookService } from '@/services/sportsbook.service';
import { CdnImg } from '../cdn-img';
import { siteService } from '@/services/site.service';
import { useHomePage } from './contexts/home-page-context';
import { NewFeatureBadge } from '@/components/new-tag/new-feature-badge';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { usePredictionMarket } from '@/hooks/use-prediction-market';
import { SINGLE_SPORT_MENU_MODE } from '@/constants/sport-menu-mode';
import { useMenuTypeList } from '@/hooks/query';
import { openAdvantPlay } from '@/services/third-party-entrance.service';

export const useRewardSystemTrigger = () => {
	const { user } = useAccountInfo();
	const triggerWithCare = (event?: string) => {
		if (user?.EnableRewardMaintenance) {
			const event = new CustomEvent('open-um');
			document.dispatchEvent(event);
		} else {
			if (!event) {
				triggerRewardSystemPanel();
			} else {
				document.dispatchEvent(new CustomEvent(event))
			}
		}
	};
	return {
		triggerWithCare
	}
}
const AccountInfo = () => {
	const authToken = useAuthToken();
	const { user } = useAccountInfo();
	const { triggerWithCare } = useRewardSystemTrigger();
	const { SiteConfigsData } = useGetSiteConfigs();
	const displayCurrencyName = SiteConfigsData?.EnableCurrencyDisplay ? user?.Curr : '';

	const handleClick = () => {
		if (user?.RewardInfo?.EnableRewardSystem) {
			const kafkaData = {
				note: KafkaNote.Reward,
				type: 1,
				action: 33
			}
			setKafkaNormal(kafkaData);
			triggerWithCare();
		}
	}

	return (
		!authToken ? null :
			!user?.RewardInfo?.EnableRewardSystem ? (
				<div
					className="home-account"
					onClick={handleClick}
				>
					<div className="home-account__info">
						<div className={clsx('home-account__name', homeStyle.username)}>{user?.DisplayUserName}</div>
						{
							user?.CanSeeAvailableFunds &&
							<div className={clsx('home-account__balance', homeStyle.balance)}>{`${displayCurrencyName} ${user?.BCredit}`}</div>
						}
					</div>
				</div>
			) : (
				<div
					className="home-account"
					data-level={user?.RewardInfo.VipLevel}
					onClick={handleClick}
				>
					<div className="vip-badge" data-level={user?.RewardInfo.VipLevel} />
					<div className="home-account__info">
						<div className="home-account__name">{user?.DisplayUserName}</div>
						{
							user?.CanSeeAvailableFunds &&
							<div className="home-account__balance">{`${displayCurrencyName} ${user?.BCredit}`}</div>
						}
					</div>
				</div>
			)
	);
};

interface OptionsProps {
	setThirdPartyProduct: (products: ProductProps[]) => void;
	setCurrentEventMode: (eventMode: EventMode) => void;
}

// check if there are non-Outright matches
const hasNonOutrightMatches = (games: MarketData[] | undefined): boolean =>
	Boolean(games?.some(game => game.E > 0 || game.T > 0 || game.L > 0));

const SWIPER_CUSTOM_PROPS = {
	navigation: true,
	allowTouchMove: true,
	spaceBetween: 3,
	breakpoints: {
		750: {
			spaceBetween: 12,
			allowTouchMove: false
		}
	}
};

export const Options = memo(function Options({
	setThirdPartyProduct,
	setCurrentEventMode
}: OptionsProps) {
	const router = useRouter();
	const locale = useLocale();
	const t = useTranslations('General');
	const { filterSabaString } = useFilterSabaString();
	const { SiteConfigsData } = useGetSiteConfigs();
	const { nonSport, enableESoccerBattle } = useLicSettings();
	const { openPredictionMarket, predictionMarketNewTagUntil } = usePredictionMarket();
	const { openCasinoRoyale } = useCasinoRoyale();
	const authToken = useAuthToken();
	const isBefore = !authToken;
	const { user } = useAccountInfo();
	const [currentProduct, setCurrentProduct] = useState('');
	const view = useAppearanceStore(state => state.view);
	const oddsPageLayout = useAppearanceStore(state => state.oddsPageLayout);
	const isNewbie = oddsPageLayout === 'list';
	const deepLinkSiteInfo = useSiteStore(state => state.deepLinkSiteInfo);
	const forbiddenSport = getForbiddenSport(view);
	const { menuResponse } = useOriginMenuData({ isNeedParlayData: false });
	const { setIsMainProductSelected, homePageProductsOfEventMode } = useHomePage();
	const { menuTypeList } = useMenuTypeList();

	const availableEventModes = useMemo(() => {
		const order = Object.keys(menuDefinition);
		const modes = Object.entries(homePageProductsOfEventMode)
			.filter(([eventMode, modeData]) =>
				// 一般sport (M0) 賽事不做過濾
				eventMode === 'M0' || 
				hasNonOutrightMatches(modeData.n) || 
				hasNonOutrightMatches(modeData.p)
			)
			.map(([eventMode]) => eventMode as EventMode)
			.sort((a, b) => order.indexOf(a) - order.indexOf(b));

		if (!modes.includes('M0')) {
			modes.unshift('M0');
		}

		return modes;
	}, [homePageProductsOfEventMode]);

	const available3rdPartyGames = useMemo(() => {
		const gameIds = new Set<number>();

		if (user?.LicenseeSiteSetting?.EnableBetbyBasketball2K) {
			gameIds.add(134);
		}
		if (user?.LicenseeSiteSetting?.EnableBetbyEsoccer) {
			gameIds.add(116);
		}

		for (const rawMenuData of menuResponse) {
			const { GameId } = rawMenuData;

			if (isNumberGames(GameId) || sportService.isVirtualSports(GameId)) {
				gameIds.add(GameId);
			}
		}

		return [...gameIds];
	}, [
		menuResponse, 
		user?.LicenseeSiteSetting?.EnableBetbyBasketball2K, 
		user?.LicenseeSiteSetting?.EnableBetbyEsoccer, 
	]);

	const checkProductEnabled = useCallback((product: Product | SubProduct): boolean => {
		if (product.enabled !== undefined) {
			return product.enabled;
		}

		const params: ProductConditionParams = {
			available3rdPartyGames,
			virtualSoccerList: nonSport?.virtualSoccerList,
			canSeeCasinoRoyale: SiteConfigsData?.CanSeeCasinoRoyale === true,
			canSeePredictionMarket: nonSport?.canSeePredictionMarket === true,
			enableSabaScore: SiteConfigsData?.EnableSabaScore === true,
			forbiddenSport,
			isEuroCupEnabled: availableEventModes.indexOf('M3') !== -1,
			siteId: SiteConfigsData?.GetMessageID ?? '0',
			user,
			isNewbie,
			enableESoccerBattle: enableESoccerBattle ?? false,
			canSeeAdvantPlayVendorLobby: Boolean(user?.CanSeeAdvantPlayVendorLobby || SiteConfigsData?.CanSeeAdvantPlayVendorLobby),
		};

		if (product.subProducts) {
			return product.subProducts.some(
				subProduct => subProduct.enableCondition?.(params)
			);
		}

		return product.enableCondition?.(params) ?? false;
	}, [
		available3rdPartyGames,
		nonSport?.virtualSoccerList,
		SiteConfigsData?.CanSeeCasinoRoyale,
		nonSport?.canSeePredictionMarket,
		SiteConfigsData?.EnableSabaScore,
		forbiddenSport,
		availableEventModes,
		isNewbie,
		enableESoccerBattle,
		SiteConfigsData?.GetMessageID,
		user,
		SiteConfigsData?.CanSeeAdvantPlayVendorLobby,
	]);

	const products: Product[] = useMemo(() => {
		if (!availableEventModes.length) return [];

		const mapEventMode = (menuType: EventMode) => {
			const modeId = menuType.replace(/^M(\d+)$/, '$1');
			const isAllSports = modeId === '0';

			return {
				productName: `M${modeId}`,
				image: {
					href: sportService.getHomeEventModeIcon(modeId),
				},
				resourceKey: isAllSports
					? 'lbl_Sports'
					: sportsbookService.makeMenuNodeResourceKey(SiteConfigsData?.EnableFilterSaba, modeId),
			};
		};

		return [
			...availableEventModes
				.filter(menuType => menuType === 'M0')
				.map(mapEventMode),
			...thirdPartyProducts
				.filter(p => p.productName === 'predictionMarket')
				.filter(checkProductEnabled),
			...availableEventModes
				.filter(menuType => !['M0', 'M27', 'M28'].includes(menuType)) // M27, M28 dont show on lobby
				.map(mapEventMode),
			...thirdPartyProducts
				.filter(p => p.productName !== 'predictionMarket')
				.filter(checkProductEnabled),
		];
	}, [availableEventModes, checkProductEnabled, SiteConfigsData?.EnableFilterSaba]);

	useEffect(() => {
		if (products.length > 0) {
			if (!currentProduct || products.every(existingProduct => existingProduct.productName !== currentProduct)) {
				onOptionClick(0);
			}
		}
	}, [currentProduct, products]);

	// 抽出product顯示邏輯
	const handleVisibleProducts = (selectedOption: Product) => {
		if (!selectedOption.subProducts) return [];
		
		const visibleProductProps = selectedOption.subProducts.filter(checkProductEnabled);
		if (visibleProductProps.length === 0) return [];
		
		const processedProducts = [...visibleProductProps];
		if (selectedOption.productName === 'virtualSports' && user?.VSTournament && user?.ActStatus == 2) {
			processedProducts.unshift({
				productName: 'vsTournament',
				GameId: 'virtual-tournament',
				GlobalResourceLabel: 'lbl_VSTournament',
				RouteTarget: '/third-party/vs-tournament'
			});
		}
		
		const sortedProducts = (SiteConfigsData?.CanSeeVS2Tournament ?? false) 
			? sortVirtualSports(processedProducts.map(product => ({ GameId: product.GameId })))
				.map(sortedItem => processedProducts.find(product => product.GameId === sortedItem.GameId)!)
			: processedProducts;
		
		return sortedProducts.map(productProps => ({
			...productProps,
			GlobalResourceLabel: t(productProps.GlobalResourceLabel)
		}));
	};

	useEffect(() => {
		if (currentProduct) {
			const selectedOption = products.find(p => p.productName === currentProduct);
			if (selectedOption?.subProducts) {
				const visibleProductProps = handleVisibleProducts(selectedOption);
				visibleProductProps.length > 0 && setThirdPartyProduct(visibleProductProps);
			}
		}
	}, [view]);

	const onOptionClick = (index: number) => {
		const selectedOption = products[index];
		
		if (selectedOption) {
			const { productName } = selectedOption;
			setKafka(productName);
			setCurrentProduct(productName);

			wcPromotionMissionService.notifyMission({
				taskType: TaskTypeEnum.ClickVirtualSports,
				productCode: productName
			});

			// 單一運動選單，直接跳轉到第一個子選單
			if (SINGLE_SPORT_MENU_MODE.includes(productName as EventMode)) {
				const modeId = Number(productName.replace(/^M(\d+)$/, '$1'));
				const path = menuTypeList.find(item => item.modeId === modeId)?.path;
				if (path) {
					router.push(path, { locale });
				}
				return;
			}

			if (selectedOption.subProducts) {
				const visibleProductProps = handleVisibleProducts(selectedOption);
				if (visibleProductProps.length > 0) {
					setThirdPartyProduct(visibleProductProps);
					setIsMainProductSelected(false);
				}
			} else if (/^M\d+$/.test(productName)) {
				const eventMode = productName as EventMode;
				setIsMainProductSelected(true);
				setCurrentEventMode(eventMode);
				
			} else {
				if (productName === 'sabaScore') {
					router.push('/score', { locale })
				} else if (productName === 'casinoRoyale') {
					openCasinoRoyale({ isApp: deepLinkSiteInfo.isApp });
				} else if (productName === 'advantplay') {
					openAdvantPlay({ isApp: deepLinkSiteInfo.isApp }, authToken ?? undefined);
				} else if (productName === 'predictionMarket') {
					openPredictionMarket();
				} else if (productName === 'exchange') {
					openExchangeInNewWindow(isBefore, user, SiteConfigsData, t('lbl_deposit'));
				}
			}
		}
	}

	const setKafka = (productName: string) => {
		const isGalaxy = view === ViewEnum.galaxy;

		if (productName === 'predictionMarket') {
			setKafkaNormal({ note: KafkaNote.PredictionMarket, type: 1, action: 5 });
			return;
		}
		
		if (productName === 'M50') {
			setKafkaNormal({ note: isGalaxy ? KafkaNote.GalaxyV2 : KafkaNote.MarsV1, type: isGalaxy ? 16 : 1, action: isGalaxy ? 3 : 26 });
			return;
		}
		if (productName === 'M51') {
			setKafkaNormal({ note: isGalaxy ? KafkaNote.GalaxyV2 : KafkaNote.MarsV1, type: isGalaxy ? 16 : 1, action: isGalaxy ? 4 : 28 });
			return;
		}
		if (productName === 'M52') {
			setKafkaNormal({ note: isGalaxy ? KafkaNote.GalaxyV2 : KafkaNote.MarsV1, type: isGalaxy ? 16 : 1, action: isGalaxy ? 5 : 27 });
			return;
		}
		if (productName === 'M2') {
			setKafkaNormal({ note: isGalaxy ? KafkaNote.GalaxyV2 : KafkaNote.MarsV1, type: isGalaxy ? 16 : 1, action: isGalaxy ? 1 : 25 });
			return;
		}

		if (view !== ViewEnum.mars || window.innerWidth >= 750) return; // 只計 mars 且寬度小於 750

		let action;
		switch (productName) {
			case 'exchange':
				action = 2;
				break;
			case 'casinoRoyale':
				action = 3;
				break;
			case 'sabaScore':
				action = 4;
				break;
		}
		if (action) {
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: 1,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	const slideElements = products.map(product => {
		const baseHref = product.image.href;
		const showPredictionMarketNewTag = product.productName === 'predictionMarket' && 
			predictionMarketNewTagUntil;

		return (
			<Fragment key={product.productName}>
				<div
					className="home-nav__item-bg"
					style={{
						maskImage: `url(${siteService.combineImagePath('/images/home/nav/icon-bg.png', true, SiteConfigsData)})`,
						WebkitMaskImage: `url(${siteService.combineImagePath('/images/home/nav/icon-bg.png', true, SiteConfigsData)})`
					}}
				/>
				<CdnImg
					key={product.productName}
					className="home-nav__icon"
					src={baseHref}
					isGalaxyFolder={product.image.inGalaxyFolder ?? true}
				/>
				<div className="home-nav__title">{filterSabaString(t(product.resourceKey))}</div>
				{showPredictionMarketNewTag && (
					<NewFeatureBadge
						className="home-nav__new-badge"
						variant="new"
						timeRange={{ to: predictionMarketNewTagUntil }}
					/>
				)}
			</Fragment>
		);
	});

	return (
		<div className="home-option">
			<AccountInfo />
			<SwiperContainer
				slideElements={slideElements}
				className="home-nav"
				elementClassName={clsx('home-nav__item', homeStyle.navItem)}
				onSlideClick={onOptionClick}
				currentIndex={products.findIndex(product => product.productName === currentProduct)}
				customProps={SWIPER_CUSTOM_PROPS}
			/>
		</div>
	);
});